import './classic-learning.scss';
import React, { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Trans, useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';

import * as images from './classic-learning-media'
import StopMotionSvg from '../../components/StopMotionSvg';
import DashedDrawedSvg from '../../components/DashedDrawedSvg';
import CommonButton from '../../components/button/button';
import useDeviceDetection from '../../hooks/useDeviceDetection';
import ModalOpenButton from '../../components/common/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';


function ClassicLearning() {

    const { reloadState, relativeVh } = useOutletContext();

    const { t } = useTranslation();
    const [intervalSection2, setIntervalSection2] = useState(null);
    const deviceType = useDeviceDetection();

    useLayoutEffect(() => {
        window.scrollTo({ top: 0 });
        reloadState[1](false);
    }, []);

    useGSAP(() => {

        //#region section 1

        gsap.to('#classic-learning-header', {
            scrollTrigger: {
                trigger: '#section1',
                start: 'top top',
                end: 'bottom top',
                scrub: true,
            },
            scale: 1.81
        });
        //#endregion

        //#region section 2
        const tl = gsap.timeline();
        ScrollTrigger.create({
            animation: tl,
            trigger: '.section-2',
            start: "top 70%",
            end: '10% 20%',
            scrub: true
        });
        tl.fromTo(".section-2 .row .item",
            {
                autoAlpha: 0,
                y: 100,
            },
            {
                autoAlpha: 1,
                y: 0,
                duration: 1,
                stagger: .35
            }
        )
            .add(() => setIntervalSection2(i => !i ? 400 : null), '-=0.2')
        //#endregion

        //#region section 8 woman skateboard entering
        gsap.timeline({
            scrollTrigger: {
                trigger: '#background-image',
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
            }
        }).fromTo(
            '#skateboard',
            { xPercent: 100, y: -30 }
            , {
                xPercent: 20, y: 0, opacity: 1, duration: 4,// ease: 'expo.out'
            }
        ).to('#skateboard',
            {
                xPercent: (-50),
                opacity: 1,
                duration: 4,
                ease: 'expo.inOut'
            })


        //#endregion

        gsap.timeline({ repeat: -1 })
            .to('.play-circle-button, .button', { scale: 1.3 }, 0)
            .to('.play-circle-button, .button', { scale: 1 }, 1.2)

        //#region section 8 

        // green header entering
        gsap.from('.section-8 .marked-text', {
            scrollTrigger: {
                trigger: '.section-8 .marked-text',
                start: 'top bottom',
                endTrigger: '.section-7',
                end: 'top 10%',
                scrub: true,
            },
            scale: (130 / 88),
            ease: 'power1.out'
        });
        //#region dancing items

        // section 14
        gsap.timeline({ repeat: -1 })
            .set('.section-14-images div img', { x: -24 }, 0)
            .set('.section-14-images div img', { x: 24 }, 0.9)
            .set('.section-14-images div img', { x: -24 }, 1.8)

        //#endregion

        setTimeout(() => {
            // Ensure ScrollTrigger recalculates positions
            ScrollTrigger.refresh();
        }, 100);

    }, [])

    const comeToPlayButtonNote = <span className={'notes'}>{t('classic-learning.A cool macro is waiting for you here')}</span>;
    const comeToPlayButtonContainer = <div className='lamp-button-container flex'>
        {!deviceType?.isMobile && comeToPlayButtonNote}
        {images.lampBtn.arrow}
        <ModalOpenButton modalType={'fade'} content={{ iframe: 'https://360.articulate.com/review/content/7d98fb9a-7b1c-4b57-b745-8c875ab28a5b/review' }}
            triggerButton={onClick =>
                <button onClick={onClick} type='button' aria-label='לצפיה בטרנדים החמים' tabIndex="true" >
                    {images.lampBtn.circle}
                    <img src={images.lampBtn.lamp} alt="" />
                </button>
            } />

    </div>;

    return (
        <div className='classic-learning-component'>
            {/* section 1 */}
            <section id="section-1">
                <h1 id="classic-learning-header" className='headline'>
                    <Trans i18nKey={'classic-learning.classic-learning'}>
                        <span className='intro-title'></span>
                    </Trans>
                </h1>
            </section>

            {/* section 4 */}
            <section className='section-4 background-image-included' id="section4">
                <p className='sub-title '>{t('classic-learning.Psst looking for classic learning')}</p>
            </section>

            {/* section 2 */}
            <section className='section-2' id="section2">
                <div className='sub-title'>
                    <span className='marked-text'>{t('classic-learning.Then you have found it')}</span>
                </div>
                <div className='productions-types' id='productions-types'>
                    <div className="row">
                        {images.section2Images.map((item, index) => (
                            <div className="item" key={`item${index}`}>
                                <div className='dancing-lines-container' width={item.width} height={item.height}
                                    style={{
                                        marginInline: item.marginInline, marginBlock: item.marginBlock,
                                        ...(item.alignSelf && { alignSelf: item.alignSelf })
                                    }}>
                                    <StopMotionSvg
                                        shapes={item.shapes}
                                        width={item.width}
                                        height={item.height}
                                        interval={intervalSection2}
                                    />
                                </div>
                                <div className={`related-image ${item.name}`} key={`item${index}`}>
                                    <img src={item.img} alt={t(`classic-learning.section-2.${item.description}`)} />
                                </div>
                                <p className={`${item.name}-p text-run`} >{t(`classic-learning.section-2.${item.description}`)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* section 3 */}
            {deviceType?.isMobile ? <div className="mobile-dash-line" id="mobile-dash-line-4">
                <DashedDrawedSvg svgWidth="195" svgHeight="202"
                    d="M13.5282 54.52C38.9556 1.25039 60.5127 163.604 97.221 79.3794C113.281 42.5302 182.436 141.28 182.649 157.704"
                    strokeDasharray={340} maskStrokDashArrayc='11' strokeWidth={11}
                    trigger='#mobile-dash-line-4' startTrigger="top 95%" endTrigger="bottom 10%" />
            </div> :
                <section className='section-3' id="section3">
                    <div className='transition-1' id="transition-1">
                        <DashedDrawedSvg svgWidth='305' svgHeight='262' strokeDasharray={340}
                            d='M5 23.7493C66.1959 -70.875 67.224 241.793 157.364 91.3253C196.801 25.4943 303.532 229.404 299.91 260.553'
                            trigger='#section3' startTrigger="top 85%" endTrigger="bottom 10%" />
                    </div>
                </section>
            }

            {/* section 8 */}
            < section className='section-8' id="section8" >
                <div className='flex-column'>
                    <p className='text-run'>{t('classic-learning.Interesting fact!')}</p>
                    <p className='sub-title'>
                        <Trans i18nKey="classic-learning.About 50 p of organizations use classic learning">
                            About <span className='marked-text'>50%</span>of organizations use classic learning
                        </Trans>
                    </p>
                </div>
                <div className='background-image' id="background-image">
                    <img id="skateboard" src={images.skateboardImg} alt='' />
                </div>
                <h3 className='sub-title'>{t('classic-learning.Classic learning with us is not what you thought')}</h3>
            </section >

            {deviceType?.isMobile && <div className="mobile-dash-line" id="mobile-dash-line-1">
                <DashedDrawedSvg svgWidth="201" svgHeight="196"
                    d="M63.1351 8.41199C115.955 -17.9374 33.0592 123.314 113.817 79.502C149.15 60.334 142.91 180.729 132.972 193.808"
                    strokeDasharray={340} maskStrokDashArrayc='11' strokeWidth={11}
                    trigger='#mobile-dash-line-1' startTrigger="top 95%" endTrigger="bottom 10%" />
            </div>}


            {/* section 6 */}
            <section className='section-6' id="section6">
                <div className='bottom-row-container flex'>
                    <div className='flex-column'>
                        <h6 className={'text-run'}>{t('classic-learning.We start with macro-planning that will surprise even the learner at the back of the class')}</h6>
                        <img src={images.youSection6} alt='' />
                    </div>
                </div>
            </section>

            {deviceType?.isMobile && <div className="mobile-dash-line" id="mobile-dash-line-2"><DashedDrawedSvg svgWidth="201" svgHeight="196" strokeDasharray={340} maskStrokDashArrayc='11' strokeWidth={11}
                d="M68.0275 2.26809C58.0899 15.3471 51.8497 135.742 87.1822 116.574C167.94 72.7621 85.0449 214.013 137.864 187.664"
                trigger='#mobile-dash-line-2' startTrigger="top 85%" endTrigger="bottom 10%" />
            </div>}


            {/* section 10 */}
            < section className='section-10' id="section10" >
                {deviceType?.isMobile &&
                    <p className={'text-run'}>{t('classic-learning.We continue with designing a presentation that will make you go WOW and elevate any workshop')}</p>

                }
                <div className='flex media-conatiner'>
                    <StopMotionSvg
                        shapes={images.bigOrangeLineDancingPathes}
                        width={144}
                        height={278}
                        interval={500}
                    />
                    <img src={images.section10Img2} alt='' />

                </div>
                <div className='flex-column'>
                    {!deviceType?.isMobile &&
                        <p className={'text-run'}>{t('classic-learning.We continue with designing a presentation that will make you go WOW and elevate any workshop')}</p>
                    }
                    <div className='flex'>
                        <ModalOpenButton content={{ commonVidoe: 'presentation', vidoePoster: images.animationPoster, text: t('classic-learning.We continue with designing a presentation that will make you go WOW and elevate any workshop') }}
                            modalType={'fade'}
                            triggerButton={onClick =>
                                <button onClick={onClick} type='button' aria-label='לצפיה בטרנדים החמים' tabIndex="true" className='play-circle-button'>
                                    <FontAwesomeIcon icon={faPlay} /></button>
                            } />
                        {images.setion10Arrow}
                    </div>
                </div>
            </section>


            {deviceType?.isMobile &&
                <div className="mobile-dash-line" id="mobile-dash-line-3">
                    <DashedDrawedSvg svgWidth="201" svgHeight="196"
                        d="M63.1351 8.37195
C115.955 -17.9774 33.0592 123.274 113.817 79.462
C149.15 60.294 142.91 180.689 132.972 193.768"    strokeDasharray={340} maskStrokDashArrayc='11' strokeWidth={11}
                        trigger='#mobile-dash-line-3' startTrigger="top 85%" endTrigger="bottom 10%" />
                </div>
            }


            <section className='flex  section-9'>
                <p className={'text-run'}>{t('classic-learning.And we package it all into a lesson plan that brings out the TED speaker in everyone')}</p>

                <img src={images.section10Img3} alt='' />

            </section >

            {/* section 14 */}
            < section className='section-14' id='section14' >
                <div className='yalla'>
                    <div className='sub-title'>
                        <Trans i18nKey="classic-learning.In short classic learning is essential Just make sure it is in class">
                            In short you focus on the content and we will <span className='marked-text'>take care</span>
                        </Trans>
                    </div>
                    <div className='flex'>
                        <CommonButton
                            text={t("classic-learning.Class me")}
                            routePath='/contact-us'
                            containerPage="classic learning"
                        />
                        {images.section14Decorations.lines}
                    </div>
                </div>
                <div className='section-14-images'>
                    <div>
                        <img src={images.section14Decorations.image} alt='' />
                        {images.section14Decorations.greenSvg}
                    </div>
                    <img src={images.section14Decorations.tringles} alt='' />

                </div>
            </section >


        </div >);
}

export default ClassicLearning;

